import { useState, useCallback } from 'react';
import { useAgeGate } from '@/contexts/AgeGateContext';
import { useBranding } from '@/contexts/BrandingContext';
import { Compass, Check } from 'lucide-react';

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
const days = Array.from({ length: 31 }, (_, i) => i + 1);

export default function AgeGateModal() {
  const { verifyAge } = useAgeGate();
  const { brandName } = useBranding();
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const dobComplete = month !== "" && day !== "" && year !== "";
  const canEnter = dobComplete && confirmed;

  const handleEnter = useCallback(() => {
    if (!canEnter) return;
    const birthDate = new Date(parseInt(year), parseInt(month), parseInt(day));
    verifyAge(birthDate);
  }, [canEnter, month, day, year, verifyAge]);

  const selectClass = (hasValue) => `
    w-full appearance-none bg-zinc-800 border rounded-xl px-4 py-3.5 text-base
    focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500
    transition-colors cursor-pointer
    ${hasValue ? 'text-white border-zinc-600' : 'text-zinc-500 border-zinc-700'}
  `;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm"
      data-testid="age-gate-modal"
    >
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative w-full max-w-md mx-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-600 to-amber-700 px-8 py-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Compass className="w-10 h-10 text-white" />
              <span className="font-heading text-3xl font-bold text-white tracking-tight">
                {brandName}
              </span>
            </div>
            <p className="text-amber-100 text-sm">
              Self-Serve Craft Beer Experience
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Welcome!
              </h2>
              <p className="text-zinc-400">
                Please enter your date of birth to continue.
              </p>
            </div>

            {/* Date of Birth Selects */}
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="dob-inputs">
              <div>
                <label className="block text-zinc-500 text-xs font-medium mb-1.5 uppercase tracking-wider">Month</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className={selectClass(month !== "")}
                  data-testid="dob-month"
                >
                  <option value="">MM</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-zinc-500 text-xs font-medium mb-1.5 uppercase tracking-wider">Day</label>
                <select
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  className={selectClass(day !== "")}
                  data-testid="dob-day"
                >
                  <option value="">DD</option>
                  {days.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-zinc-500 text-xs font-medium mb-1.5 uppercase tracking-wider">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className={selectClass(year !== "")}
                  data-testid="dob-year"
                >
                  <option value="">YYYY</option>
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Confirmation Checkbox — only after DOB is entered */}
            {dobComplete && (
              <div className="mb-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <label 
                  className="flex items-start gap-4 cursor-pointer group"
                  data-testid="age-confirmation-label"
                >
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={confirmed}
                    onClick={() => setConfirmed(!confirmed)}
                    className={`
                      w-7 h-7 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                      transition-all duration-200
                      ${confirmed 
                        ? 'bg-amber-500 border-amber-500' 
                        : 'bg-transparent border-zinc-600 group-hover:border-zinc-500'
                      }
                    `}
                    data-testid="age-confirmation-checkbox"
                  >
                    {confirmed && <Check className="w-5 h-5 text-black" strokeWidth={3} />}
                  </button>
                  <span className="text-zinc-300 text-base leading-relaxed">
                    I confirm that I am at least <span className="text-amber-500 font-semibold">21 years old</span> and of legal drinking age in my jurisdiction.
                  </span>
                </label>
              </div>
            )}

            {/* Enter button */}
            <button
              onClick={handleEnter}
              disabled={!canEnter}
              className={`
                w-full py-4 rounded-xl font-bold text-lg
                transition-all duration-200
                ${canEnter
                  ? 'bg-amber-500 hover:bg-amber-400 text-black shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02]'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                }
              `}
              data-testid="age-gate-enter-button"
            >
              Enter {brandName}
            </button>

            <p className="text-center text-zinc-600 text-xs mt-6">
              By entering, you agree to our Terms of Service and confirm you are of legal drinking age.
            </p>
          </div>
        </div>

        <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
}
